import { useEffect, useMemo, useState } from 'react';

import { useDispatch } from "react-redux";

import { useNavigate } from 'react-router';

import { Box, Button, CircularProgress, Grid, List, ListItem, ListItemText, ListSubheader, Typography } from '@mui/material';

import UndoIcon from '@mui/icons-material/Undo';

import { Eventcalendar, setOptions, localeEs, MbscEventcalendarView, MbscCellClickEvent, MbscCalendarInvalid, } from '@mobiscroll/react';
import '@mobiscroll/react/dist/css/mobiscroll.min.css';

import moment from 'moment';
import 'moment/locale/es';

import { openSnackbar } from "../../store/slices/SnackbarSlice";

import { Layout } from '../../components/Layout';

import { AgregarCalendario, Calendario } from '../../connections/admin/CalendarioConnection';

import '../../css/Calendar.css';

setOptions({
    locale: localeEs,
    theme: 'ios', 
    themeVariant: 'light',
});

export const CalendarioPage = () => {

    const dispatch = useDispatch();

    const navigate = useNavigate();

    const [inhabiles, setInhabiles] = useState<Date[]>([]);
    const [inhabilesFull, setInhabilesFull] = useState<Date[]>([]);

    const view: MbscEventcalendarView = { calendar: { type: 'month', outerDays: false, }, };

    const [loading, setLoading] = useState( true );
    const [loadingGuardar, setLoadingGuardar] = useState( false );

    const myInvalid: MbscCalendarInvalid[] = useMemo(() => [
            {                
                recurring: {
                    repeat: 'weekly',
                    weekDays: 'SA,SU'
                },		
                cssClass: 'md-lunch-break-class mbsc-flex',
            },
        ], [],
    );    

    const groupedInhabiles = useMemo(() => {

        const sorted = [ ...inhabiles ].sort( ( a, b ) => a.getTime() - b.getTime() );

        const grouped: Record<string, Date[]> = {};

        sorted.forEach((date) => {
            const key = date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
            });

            if (!grouped[key]) grouped[key] = [];
                grouped[key].push(date);
        });

        return grouped;

    }, [inhabiles]);

    const handleDayClick = ( event: MbscCellClickEvent ) => {

        const clicked = event.date;
        const dayOfWeek = clicked.getDay();

        if ( dayOfWeek === 0 || dayOfWeek === 6 ) {
            return;
        }

        const exists = inhabiles.some( (d) => moment(d).format('DD-MM-YYYY') === moment(clicked).format('DD-MM-YYYY') );

        if (exists) {
            setInhabiles( inhabiles.filter( (d) => moment(d).format('DD-MM-YYYY') !== moment(clicked).format('DD-MM-YYYY') ) );
        } 
        else {
            setInhabiles([ ...inhabiles, clicked ]);
        }
    };

    const handleGuardarCalendario = async () => {

        setLoadingGuardar( true );

        await AgregarCalendario({ fechas: inhabiles }).then( resp => {

            const { success, message } = resp;

            setTimeout(() => {
                
                if( success ){
                    dispatch( openSnackbar({ message: message, variant: 'success' }));   
                }
                else {
                    dispatch( openSnackbar({ message: message, variant: 'error' }));   
                }

                setLoadingGuardar( false );

            }, 700);

        });

    }

    useEffect(() => {
      
        async function Obtener() {
            
            await Calendario().then( resp => {

                setTimeout(() => {
                    
                    const fechas = resp.map( (elem) => new Date( elem.fecha.toString() ) );

                    setInhabiles( fechas );
                    setInhabilesFull( fechas );

                    setLoading( false );

                }, 700);

            });

        }

        Obtener();

    }, [])

    return (

        <Layout breadcrumbs={[ { text: 'Administración' }, { text: 'Calendario de días inhabiles' } ]} backgroundPaper={ true }>

            {
                loading
                ?
                    <Grid size={{ md: 8, xs: 12 }} textAlign={'center'} alignContent={'center'} height={600}>
                        <CircularProgress size={32} />
                    </Grid>
                :
                    <Grid container spacing={3}>
                                        
                        <Grid size={{ xs: 12 }} textAlign={'start'}>

                            <Button startIcon={ <UndoIcon /> } color="secondary" variant="text" sx={{ mt: 1 }} onClick={ () => navigate( '/' ) }> 
                                Regresar
                            </Button> 

                        </Grid>   
                        
                        <Grid size={{ md: 8, xs: 12 }} sx={{ height: 600 }}>

                            <Eventcalendar
                                view={ view }
                                invalid={ myInvalid }
                                clickToCreate={ false }
                                dragToCreate={ false }
                                dragToMove={ false }
                                dragToResize={ false }
                                onCellClick={ handleDayClick }
                                renderDay={( args ) => {
                                
                                    const date = new Date(args.date);

                                    const isToday = date.toDateString() === new Date().toDateString();
                                    const dayOfWeek = date.getDay();

                                    const isInhabil = inhabiles.some( (d) => d.toDateString() === date.toDateString() );

                                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                                    return (
                                        <div
                                            style={{
                                                backgroundColor: isInhabil ? '#0A192D' : isToday ? 'rgba(10, 25, 45, 0.15)' : '',
                                                borderRadius: '5px',
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '400',
                                                color: ( isInhabil  ) ? '#fff' : '#111',
                                                transition: 'all 0.3s ease',
                                                cursor: isWeekend ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            { args.date.getDate() }
                                        </div>
                                    );
                                }}
                            />

                        </Grid>        
                
                        <Grid size={{ md: 4, xs: 12 }}>

                            <Grid container spacing={3}>

                                <Grid size={{ md: 12, xs: 12 }}>
                                
                                    <Typography variant='h5' textAlign={'center'} mb={2}>Días inhábiles</Typography>

                                    {
                                        Object.keys( groupedInhabiles ).length === 0 
                                        ? 
                                            <Box sx={{ height: 480 }} textAlign={'center'} alignContent={'center'}>
                                                <Typography variant='caption' color='textSecondary' sx={{ mt: 10 }}>
                                                    No se agregaron días inhabiles
                                                </Typography>
                                            </Box>
                                        :                    
                                            <List
                                                sx={{
                                                    width: '100%',                          
                                                    position: 'relative',
                                                    overflow: 'auto',
                                                    height: 470,
                                                }}
                                                subheader={ <li /> }                            
                                            >
                                                {
                                                    Object.entries( groupedInhabiles ).map( ( [ mes, fechas ], index ) => (

                                                        <div key={`section-${ index }`}>

                                                            <ListSubheader sx={{ fontWeight: 'bold', fontSize: 16, bgcolor: '#0A192D', color: 'white', borderRadius: 3 }}>{ mes.charAt(0).toUpperCase() + mes.slice(1) }</ListSubheader>

                                                            {
                                                                fechas
                                                                .map(( d, i ) => (
                                                                    <ListItem key={`item-${ index }-${i}`} divider>
                                                                        <ListItemText 
                                                                            primary={
                                                                                d.toLocaleDateString('es-ES', {
                                                                                    weekday: 'long',
                                                                                    day: 'numeric',
                                                                                    month: 'long',
                                                                                    year: 'numeric',
                                                                                })
                                                                            } 
                                                                        />
                                                                    </ListItem>
                                                                ))
                                                            }

                                                        </div>
                                                    ))
                                                }

                                            </List>      
                                    }    

                                </Grid>

                                <Grid size={{ md: 12, xs: 12 }}>                        

                                    <Button 
                                        fullWidth
                                        sx={{ mt: 1 }} 
                                        variant="contained" 
                                        loading={ loadingGuardar }
                                        onClick={ handleGuardarCalendario }
                                        disabled={ inhabiles.length === inhabilesFull.length && inhabiles.every( array => inhabilesFull.includes( array ) ) }
                                    > 
                                        Guardar
                                    </Button> 
                                
                                </Grid>                        
                            
                            </Grid>                

                        </Grid>         

                    </Grid>       
            }
        
        </Layout>
    );
};
